import { temperatureUnits, pressureUnits } from '../../utilities/units';
import { PulseBaseProvider } from '../../base-classes/index';

const _getMapMetadataPath = '/maps/overlays/v3/metadata';
const _getMapLayersPath = '/giv/layers/v1';
const _getLayerLegendPath = '/giv/presentation/legenddata';
const _getAlertsLegendPath = '/giv/presentation/alertskey';

const mapMetadataCacheKey = 'mapMetadata';
const mapLegendCacheKey = 'mapLegendData';
const mapAlertsLegendCacheKey = 'mapAlertsLegendData';
const mapLayerListCacheKey = 'mapLayerList';

// Cache metadata calls for 5 minutes
const metadataCacheDuration = 300000;
const mapLayerListCacheDuration = 300000;

export class MapProvider extends PulseBaseProvider {
    constructor(baseUrl, authId, secret, options = { timeout: 5000 }, logger) {
        super(baseUrl, authId, secret, options, logger);
        this.MetersPerPixelForZoom = {
            '0': 156412,
            '1': 78206,
            '2': 39103,
            '3': 19551,
            '4': 9776,
            '5': 4888,
            '6': 2444,
            '7': 1222,
            '8': 610.984,
            '9': 305.492,
            '10': 152.746,
            '11': 76.373,
            '12': 38.187,
            '13': 19.093,
            '14': 9.547,
            '15': 4.773,
            '16': 2.387,
            '17': 1.193,
            '18': 0.596,
            '19': 0.298
        };

        this.storage.sync[mapMetadataCacheKey] = this.mapMetadataSyncer.bind(this);
        this.storage.sync[mapLegendCacheKey] = this.mapLegendSyncer.bind(this);
        this.storage.sync[mapAlertsLegendCacheKey] = this.mapAlertsLegendSyncer.bind(this);
        this.storage.sync[mapLayerListCacheKey] = this.mapLayerListSyncer.bind(this);
    }

    async mapMetadataSyncer(params) {
        const { id, syncParams, resolve, reject } = params;
        let queryParams = {
            ...syncParams.stdParameters
            , verbose: syncParams.verbose
            , lid: syncParams.lid
        };

        return await super.syncData(syncParams.key, id, queryParams, _getMapMetadataPath, resolve, reject, null, syncParams.pulseApi, null, null, true, metadataCacheDuration);
    }


    async getMapMetadata(layerId, stdParameters, verbose, refresh) {
        const syncParams = {
            lid: layerId,
            verbose: verbose || false
        };

        const id = JSON.stringify(syncParams);
        syncParams.stdParameters = stdParameters;

        if (refresh)
        {
            super.removeFromStorage(mapMetadataCacheKey, id);
        }
        return await super.loadFromStorage(mapMetadataCacheKey, id, syncParams);
    }

    async mapLayerListSyncer(params) {

        const { id, syncParams, resolve, reject } = params;
        let queryParams = {
            ...syncParams.stdParameters
            , viewedLocationLatitude: syncParams.viewedLocationLatitude
            , viewedLocationLongitude: syncParams.viewedLocationLongitude
            , compact: false

        };

        const responseValidator = response => {
            return !!response;
        };

        return await super.syncData(syncParams.key, id, queryParams, _getMapLayersPath, resolve, reject, responseValidator, syncParams.pulseApi, null, null, true, mapLayerListCacheDuration);
    }

    async getMapLayers(latitude, longitude, stdParameters, tempUnit, pressureUnit) {
      
        const syncParams = {
            viewedLocationLatitude: latitude,
            viewedLocationLongitude: longitude
        };

        const id = JSON.stringify(syncParams);

        syncParams.stdParameters = stdParameters;
        syncParams.compact = false;
        const response = await super.loadFromStorage(mapLayerListCacheKey, id, syncParams);
        const layers = response.layers;
        if (layers && layers.length > 0) {
            if (tempUnit !== temperatureUnits.celsius) {
                const tempLayer = layers.find(x=> x.id === 'Observed.Temperature');
                if (tempLayer) {
                    tempLayer.id += '.English';
                }
            }

            if (pressureUnit !== pressureUnits.millibars) {
                const pressureLayer = layers.find(x=> x.id === 'Observed.Pressure.SeaLevel');
                if (pressureLayer) {
                    pressureLayer.id += '.English';
                }
            }
        }
        return response;
    }

    async mapLegendSyncer(params) {
        const { id, syncParams, resolve, reject } = params;
        const { stdParameters, lid } = syncParams;
        const queryParams = {
            ...stdParameters,
            compact: false,
            lid
        };
        const responseValidator = response => {
            return !!response && (!!response.r || !!response.Result);
        };
        return await super.syncData(syncParams.key, id, queryParams, _getLayerLegendPath, resolve, reject, responseValidator, syncParams.pulseApi, null, null, true, metadataCacheDuration);
    }


    async getLayerLegend(layerId, stdParameters, refresh) {
        const syncParams = {
            lid: layerId
        };
        const id = JSON.stringify(syncParams);
        syncParams.stdParameters = stdParameters;

        if (refresh) {
            super.removeFromStorage(mapLegendCacheKey, id);
        }
        return await super.loadFromStorage(mapLegendCacheKey, id, syncParams);
    }

    async mapAlertsLegendSyncer(params) {
        const { id, syncParams, resolve, reject } = params;
        const { stdParameters, zoomlevel, mapCenterLat, mapCenterLng, t, north, east, south, west, width, height } = syncParams;
        //const zoomlevel = this.MetersPerPixelForZoom[mapZoomLevel] / 138.88888888;
        let queryParams = {
            ...stdParameters,
            lid: 'dta:nws',
            zoomlevel,
            mapCenterLat,
            mapCenterLng,
            t
        };
        if (north && east && south && west) {
            queryParams = {
                ...queryParams,
                north,
                east,
                south,
                west
            };
        } else if (width && height) {
            queryParams = {
                ...queryParams,
                height: Math.round(height),
                width: Math.round(width)
            };
        }
        const responseValidator = response => {
            return !!response && (!!response.r || !!response.Result);
        };
        return await super.syncData(syncParams.key, id, queryParams, _getAlertsLegendPath, resolve, reject, responseValidator, syncParams.pulseApi, null, null, true, metadataCacheDuration);
    }

    async getAlertsLegend(width, height, north, east, south, west, latitude, longitude, timeslot, mapZoomLevel, stdParameters, refresh) {
        let syncParams = {
            zoomlevel: mapZoomLevel,
            mapCenterLat: latitude,
            mapCenterLng: longitude,
            t: timeslot,
            north,
            east,
            west,
            south,
            height,
            width
        };
        const id = JSON.stringify(syncParams);
        syncParams.stdParameters = stdParameters;

        if (refresh) {
            super.removeFromStorage(mapAlertsLegendCacheKey, id);
        }
        return await super.loadFromStorage(mapAlertsLegendCacheKey, id, syncParams);
    }
}
